<script setup>
import Top from './Top.vue'
import Bottom from './Bottom.vue'
import { useOrderStore } from '@/stores/OrderStore';
import { useAuthStore } from '@/stores/authStore';
import { computed } from 'vue';
import { useToast } from 'vue-toastification';

const authStore = useAuthStore();
const orderStore = useOrderStore();
const toast = useToast();

const isLoggedIn = computed(() => authStore.isUserLoggedIn || authStore.isManagerLoggedIn);

// A loaded manual order with no payments can be converted on the cart into a regular order.
const canConvertLoadedManualOrder = computed(() =>
  isLoggedIn.value
  && orderStore.state.isManualOrder
  && Boolean(orderStore.state.InvoiceId)
  && !orderStore.isThisPaidManualOrder
  && !orderStore.state.addManualOrder
);

function convertToRegularOrder() {
  const result = orderStore.convertLoadedManualOrderToRegular();
  if (!result.ok) {
    toast.error(result.reason);
    return;
  }
  toast.info(`${result.manualOrderId} is now a regular order. Edit the cart, then checkout to replace the manual order.`);
}

function undoConvert() {
  orderStore.undoConvertLoadedManualOrder();
}

</script>

<template>
    <!-- A paid manual order is read-only: inert blocks keyboard focus as well as the mouse
         (pointer-events-none alone still lets Tab reach the qty/discount inputs). -->
    <div class="w-6/12 relative flex flex-col justify-between rounded-2xl mt-7 mb-5 bg-white"
      :inert="orderStore.isThisPaidManualOrder"
      :class="{ 'opacity-50 pointer-events-none': orderStore.isThisPaidManualOrder }"
    >
        <!-- Manual Order Flag/Badge -->
        <div class="w-[95%] mx-auto">
          <div v-show="(authStore.isUserLoggedIn || authStore.isManagerLoggedIn) && orderStore.state.addManualOrder"
            class="absolute top-[-22px] left-[-4] z-10 bg-purple-100 border-2 border-purple-500 text-white text-xs font-semibold px-3 py-1.5 rounded-xl flex items-center gap-1.5 transform  origin-top-left">
            <i class="pi pi-shopping-cart text-purple-500" style="font-size: 12px;"></i>
            <span class="text-purple-500">Manual Order</span>
          </div>

          <!-- Loaded unpaid manual order: offer to convert it into an editable regular order -->
          <div v-if="canConvertLoadedManualOrder"
            class="absolute top-[-22px] left-[-4] z-10 bg-purple-100 border-2 border-purple-500 text-xs font-semibold px-3 py-1.5 rounded-xl flex items-center gap-3 origin-top-left">
            <span class="flex items-center gap-1.5 text-purple-500">
              <i class="pi pi-download" style="font-size: 12px;"></i>
              Manual Order {{ orderStore.state.InvoiceId }}
            </span>
            <button @click="convertToRegularOrder"
              class="bg-purple-500 hover:bg-purple-600 text-white px-2 py-1 rounded-lg flex items-center gap-1"
              title="Cancel this manual order and continue it as a regular order you can edit">
              <i class="pi pi-sync" style="font-size: 11px;"></i>
              Convert to Regular Order
            </button>
          </div>

          <!-- Converted: a regular, editable cart that replaces the manual order on checkout -->
          <div v-if="isLoggedIn && orderStore.state.convertedFromManualOrderId"
            class="absolute top-[-22px] left-[-4] z-10 bg-green-50 border-2 border-green-500 text-xs font-semibold px-3 py-1.5 rounded-xl flex items-center gap-3 origin-top-left">
            <span class="flex items-center gap-1.5 text-green-700">
              <i class="pi pi-sync" style="font-size: 12px;"></i>
              Regular Order — replaces {{ orderStore.state.convertedFromManualOrderId }} on checkout
            </span>
            <button @click="undoConvert" class="text-green-700 underline hover:text-green-900" title="Go back to the loaded manual order">
              Undo
            </button>
          </div>
        </div>
        <Top />
        <Bottom />
    </div>
</template>

<style scoped>

</style>
